import React, { useState, useRef, useEffect } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { FileText, Download, ArrowLeft, X, RotateCcw, Trash2 } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { useDeal } from '../contexts/DealContext';

interface DealLOIPage1Props {
  onBack?: () => void;
}

export const DealLOIPage1: React.FC<DealLOIPage1Props> = ({ onBack }) => {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const { selectedDeal, availableDeals, selectDeal } = useDeal();
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [buyerSignature, setBuyerSignature] = useState<string | null>(null);
  const [sellerSignature, setSellerSignature] = useState<string | null>(null);
  
  const buyerSignatureRef = useRef<SignatureCanvas>(null);
  const sellerSignatureRef = useRef<SignatureCanvas>(null);

  // Handle deal selection when URL changes
  useEffect(() => {
    if (!dealId) return;
    
    // Check if the selected deal matches the URL parameter
    if (!selectedDeal || selectedDeal.deal_id !== dealId) {
      // Try to find the deal in available deals and select it
      const deal = availableDeals.find(d => d.deal_id === dealId);
      if (deal) {
        selectDeal(deal);
      }
    }
  }, [dealId, selectedDeal, availableDeals, selectDeal]);

  // Check if we have a valid deal selected
  if (!dealId) {
    return <Navigate to="/deals" replace />;
  }

  // Show loading state while deal is being selected
  if (!selectedDeal || selectedDeal.deal_id !== dealId) {
    const deal = availableDeals.find(d => d.deal_id === dealId);
    if (deal) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading deal...</p>
          </div>
        </div>
      );
    } else {
      return <Navigate to="/deals" replace />;
    }
  }

  const handlePrint = () => {
    window.print();
  };

  const openImageModal = () => setIsImageModalOpen(true);
  const closeImageModal = () => setIsImageModalOpen(false);

  const clearSignature = (ref: React.RefObject<SignatureCanvas>) => {
    if (ref.current) {
      ref.current.clear();
    }
  };

  const saveSignature = (ref: React.RefObject<SignatureCanvas>, setSignature: (sig: string) => void) => {
    if (ref.current && !ref.current.isEmpty()) {
      const signatureData = ref.current.toDataURL();
      setSignature(signatureData);
    }
  };

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header - Only visible on screen, not in print */}
      <div className="bg-white shadow-sm border-b border-gray-100 print:hidden">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack || (() => navigate(`/deals/${dealId}/dashboard`))}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </button>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-600 rounded-lg">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">
                    Option 1 - {selectedDeal.business_name}
                  </h1>
                  <p className="text-sm text-gray-600">
                    $1.1M Deal Structure
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Print/Download
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* LOI Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 print:shadow-none print:border-none">
          {/* LOI Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              LETTER OF INTENT - OPTION 1
            </h1>
            <h2 className="text-xl text-gray-700 mb-4">
              {selectedDeal.business_name}
            </h2>
            <p className="text-gray-600">
              Date: {currentDate}
            </p>
          </div>

          {/* Deal Information */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Deal Structure - Option 1</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Business Name:</p>
                <p className="font-medium text-gray-900">{selectedDeal.business_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Purchase Price:</p>
                <p className="font-medium text-gray-900">$1,100,000</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Bank Financing:</p>
                <p className="font-medium text-gray-900">$550,000 (50%)</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Seller Financing:</p>
                <p className="font-medium text-gray-900">$550,000 (50%)</p>
              </div>
            </div>
          </div>

          {/* LOI Content */}
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <p>
              This Letter of Intent ("LOI") sets forth the basic terms and conditions under which 
              the undersigned buyer intends to acquire the business known as <strong>{selectedDeal.business_name}</strong> 
              under Option 1 financing structure.
            </p>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">1. Purchase Price and Financing</h4>
              <p>
                The total purchase price for the business shall be $1,100,000, structured as follows:
              </p>
              <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                <li>Bank financing: $550,000 (50% of purchase price)</li>
                <li>Seller financing: $550,000 (50% of purchase price)</li>
                <li>Interest rate on seller financing: 6% annually</li>
                <li>Term: 5 years with monthly payments</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">2. Payment Schedule</h4>
              <p>
                Monthly payments of approximately $10,640 will be made to the seller over a 5-year period, 
                with the final balloon payment due at the end of the term.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">3. Due Diligence</h4>
              <p>
                The buyer shall have a period of 30 days from the execution of this LOI to conduct 
                due diligence on the business, including but not limited to financial records, 
                customer contracts, and operational procedures.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">4. Exclusivity</h4>
              <p>
                During the due diligence period, the seller agrees not to solicit or entertain 
                offers from other potential buyers for the business.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">5. Binding Terms</h4>
              <p>
                This LOI is non-binding except for the exclusivity provision and confidentiality 
                requirements. The final terms of the acquisition will be set forth in a definitive 
                purchase agreement.
              </p>
            </div>
          </div>

          {/* Signatures Section */}
          <div className="mt-12 border-t border-gray-200 pt-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Signatures</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Buyer Signature */}
              <div>
                <h4 className="font-medium text-gray-900 mb-4">Buyer Signature</h4>
                <div className="border border-gray-300 rounded-lg p-4 mb-4" style={{ minHeight: '120px' }}>
                  {buyerSignature ? (
                    <div className="flex items-center justify-center h-full">
                      <img 
                        src={buyerSignature} 
                        alt="Buyer Signature" 
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 h-full flex items-center justify-center">
                      <p>Signature will appear here</p>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => openImageModal()}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    Sign
                  </button>
                  {buyerSignature && (
                    <>
                      <button
                        onClick={() => clearSignature(buyerSignatureRef)}
                        className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                      >
                        <RotateCcw className="w-3 h-3 inline mr-1" />
                        Clear
                      </button>
                      <button
                        onClick={() => setBuyerSignature(null)}
                        className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                      >
                        <Trash2 className="w-3 h-3 inline mr-1" />
                        Remove
                      </button>
                    </>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-2">Date: _______________</p>
              </div>

              {/* Seller Signature */}
              <div>
                <h4 className="font-medium text-gray-900 mb-4">Seller Signature</h4>
                <div className="border border-gray-300 rounded-lg p-4 mb-4" style={{ minHeight: '120px' }}>
                  {sellerSignature ? (
                    <div className="flex items-center justify-center h-full">
                      <img 
                        src={sellerSignature} 
                        alt="Seller Signature" 
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 h-full flex items-center justify-center">
                      <p>Signature will appear here</p>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => openImageModal()}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    Sign
                  </button>
                  {sellerSignature && (
                    <>
                      <button
                        onClick={() => clearSignature(sellerSignatureRef)}
                        className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                      >
                        <RotateCcw className="w-3 h-3 inline mr-1" />
                        Clear
                      </button>
                      <button
                        onClick={() => setSellerSignature(null)}
                        className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                      >
                        <Trash2 className="w-3 h-3 inline mr-1" />
                        Remove
                      </button>
                    </>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-2">Date: _______________</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Signature Modal */}
      {isImageModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Signature</h3>
              <button
                onClick={closeImageModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="border border-gray-300 rounded-lg p-4 mb-4">
              <SignatureCanvas
                ref={buyerSignatureRef}
                canvasProps={{
                  className: 'w-full h-32 border border-gray-200 rounded'
                }}
              />
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  saveSignature(buyerSignatureRef, setBuyerSignature);
                  closeImageModal();
                }}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Save Buyer Signature
              </button>
              <button
                onClick={() => {
                  saveSignature(sellerSignatureRef, setSellerSignature);
                  closeImageModal();
                }}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Save Seller Signature
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
